<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card flat tile variant="outlined">
          <v-row>
            <v-col class="bg-surface-light" cols="12" lg="6">
              <v-card-text
                class="d-flex flex-column justify-center h-100 py-lg-8"
              >
                <div
                  v-if="value.badges && value.badges.length"
                  class="mb-6 d-flex justify-center ga-6"
                >
                  <template v-for="badge in value.badges" :key="badge.id">
                    <BadgeImage
                      v-if="badge.badges_id"
                      :value="badge.badges_id"
                    />
                  </template>
                </div>

                <h3
                  v-if="selectedItem"
                  class="text-h5 text-sm-h4 text-center mb-4"
                >
                  <span
                    class="text-uppercase text-surface-variant font-weight-light"
                  >
                    {{ listPriceStr }} per person
                  </span>
                </h3>

                <h3
                  v-else-if="minListPrice > 0"
                  class="text-h5 text-sm-h4 text-center mb-4"
                >
                  <span
                    class="text-uppercase text-surface-variant font-weight-light"
                    >Starting at {{ priceFmt(minListPrice) }}
                  </span>
                </h3>

                <ul
                  v-if="value.sell_bullets && value.sell_bullets.length"
                  class="text-body-0 text-left ml-6 mb-2 px-lg-8"
                >
                  <li
                    v-for="(item, i) in value.sell_bullets"
                    :key="i"
                    class="mb-2"
                  >
                    <StyledText
                      :value="item.text"
                      em-class="text-primary font-weight-black"
                    />
                  </li>
                </ul>
              </v-card-text>
            </v-col>

            <v-col cols="12" lg="6">
              <v-card-text
                class="d-flex flex-column justify-center h-100 px-lg-8 py-lg-8"
              >
                <div v-if="selectedItem" class="mb-9">
                  <h3 class="text-h5 text-sm-h4 mb-3">{{ name }}</h3>

                  <p
                    class="text-body-0 text-surface-variant font-weight-regular"
                  >
                    {{ description }}
                  </p>
                </div>

                <div v-else class="mb-9">
                  <h3
                    v-if="scheduledItems.length"
                    class="text-h5 text-sm-h4 mb-3"
                  >
                    Register Here
                  </h3>

                  <p
                    class="text-body-0 text-surface-variant font-weight-regular"
                  >
                    {{ value.description }}
                  </p>
                </div>

                <div class="mb-3">
                  <v-select
                    v-if="scheduledItems.length || isFetching"
                    v-model="selectedItem"
                    :items="scheduledItems"
                    :loading="isFetching"
                    :menu-props="{ maxHeight: 500 }"
                    hide-details
                    label="Select date/time"
                    variant="outlined"
                  >
                    <template #selection>
                      <div class="text-body-2 mt-1">
                        <template v-if="startDateStr === endDateStr">
                          <span>{{ startDateStr }}</span>
                          <span> | {{ timeStr }}</span>
                        </template>

                        <template v-else>
                          <span>{{ startDateStr }} – {{ endDateStr }}</span>
                          <span> | {{ timeStr }}</span>
                        </template>
                      </div>
                    </template>

                    <template #item="{ props: p }">
                      <CartRecOccasionItem v-bind="p" />
                    </template>
                  </v-select>

                  <NoOfferingsAlert v-else />
                  <NoLongerActiveAlert v-if="isNoLongerActive" />
                </div>

                <div v-if="selectedItem">
                  <v-chip
                    v-if="
                      selectedItem.product_variant &&
                      selectedItem.product_variant.person
                    "
                    :href="`/trainers/${selectedItem.product_variant.person.page_slug}`"
                    class="ml-1 my-1"
                    color="light-blue-darken-2"
                    size="small"
                    target="_blank"
                    variant="elevated"
                  >
                    {{ selectedItem.product_variant.person.name }}</v-chip
                  >
                  <v-chip
                    v-if="
                      selectedItem.product_variant &&
                      selectedItem.product_variant.delivery_method
                    "
                    class="ml-1 my-1"
                    size="small"
                  >
                    {{
                      selectedItem.product_variant.delivery_method.name
                    }}</v-chip
                  >
                  <v-chip
                    v-if="selectedItem.is_half_day === true"
                    class="ml-1 my-1"
                    color="red"
                    size="small"
                  >
                    Half Day</v-chip
                  >
                  <v-chip
                    v-if="selectedItem.is_weekend === true"
                    class="ml-1 my-1"
                    color="red"
                    size="small"
                  >
                    Weekend</v-chip
                  >
                </div>

                <div class="mt-6">
                  <v-btn
                    v-if="scheduledItems.length"
                    :append-icon="mdiArrowRight"
                    :disabled="!selectedItem"
                    class="mr-3 mb-2"
                    color="primary"
                    size="large"
                    tile
                    @click="
                      cartBus.emit({
                        eventName: 'item_in_sell_section_add',
                        price: selectedItem.list_price_usd,
                        priceId: selectedItem.stripe_list_price_id,
                        sku: selectedItem.sku,
                        type: 'hd'
                      })
                    "
                    >Add to Cart</v-btn
                  >

                  <v-btn
                    class="mr-3 mb-2"
                    size="large"
                    tile
                    variant="outlined"
                    @click.stop="scrollToSection({ key: 'overview' })"
                    >Learn More</v-btn
                  >
                </div>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useEventBus } from '@vueuse/core'
import { useOccasion } from '#root/lib/useOccasion'
import { useOccasionsStoreOrThrow } from '#root/lib/useOccasionsStore'
import { priceFmt } from '#root/lib/utils'
import { mdiArrowRight } from '@mdi/js'

const props = defineProps({
  scrollToSection: {
    default: undefined,
    type: Function
  },
  value: {
    required: true,
    type: Object
  }
})

const isNoLongerActive = ref(false)
const selectedItem = ref(null)
const { description, endDateStr, listPriceStr, name, startDateStr, timeStr } =
  useOccasion(selectedItem)
const { indexes, isFetching, items, upcomingIndicies } =
  useOccasionsStoreOrThrow()

const scheduledItems = computed(() => {
  let indicies = upcomingIndicies.value

  if (!(indexes.value && items.value && indicies)) return []

  if (props.value && typeof props.value.code === 'string') {
    const ind =
      (indexes.value.product_code &&
        indexes.value.product_code[props.value.code.toUpperCase()]) ||
      []
    indicies = indicies.filter(i => ind.includes(i))
  }

  return indicies.map(i => ({ title: '', value: items.value[i] }))
})

const minListPrice = computed(() => {
  return scheduledItems.value && scheduledItems.value.length
    ? scheduledItems.value.reduce((acc, cur) => {
        return Math.min(cur.value.list_price_usd, acc)
      }, Number.MAX_VALUE)
    : undefined
})

watch(scheduledItems, newValue => {
  if (newValue && newValue.length && selectedItem.value === null) {
    const hash = window.location.hash
    let foundItem

    if (hash.startsWith('#e')) {
      const ebEventId = hash.substring(2)
      foundItem = newValue.find(({ value }) => value.eb_event_id === ebEventId)
      if (!foundItem) isNoLongerActive.value = true
    } else if (hash.startsWith('#h')) {
      const sku = hash.substring(2).toUpperCase()
      foundItem = newValue.find(({ value }) => value.sku === sku)
      if (!foundItem) isNoLongerActive.value = true
    }

    if (foundItem) selectedItem.value = foundItem.value
  }
})

watch(selectedItem, newValue => {
  if (newValue) isNoLongerActive.value = false
})

const cartBus = useEventBus('cart')
</script>
